import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import {
  Database,
  RefreshCw,
  Upload,
  Download,
  Check,
  AlertTriangle,
  Loader2,
  Mail,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Sparkles,
  MessageSquare,
  KeyRound,
  Copy,
  Plus,
  Ban,
  Trash2,
} from "lucide-react";

const IntegrationCard = ({ name, icon: Icon, status, configured }) => {
  const isConnected = configured && (status === "connected" || status === "running");
  return (
    <div className={`p-4 rounded-lg border ${isConnected ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" : "bg-muted/50 border-border"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${isConnected ? "text-emerald-600" : "text-muted-foreground"}`} />
        <span className="text-sm font-medium">{name}</span>
      </div>
      <div className="flex items-center gap-1">
        {isConnected ? (
          <>
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-emerald-600">Conectado</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">No configurado</span>
          </>
        )}
      </div>
    </div>
  );
};

// Ticket "API de solo lectura para Pando/SofIA" (2026-08-31): admin puede
// crear/listar/revocar claves X-API-Key para consumidores de máquina
// (GET-only, allowlist de 9 rutas — /api/version, /api/stats, /api/initiatives,
// /api/initiatives/{id}, /api/partners, /api/users, /api/patents, /api/kpis,
// /api/approvals, /api/auth/me). La clave completa solo se ve una vez, al crearla.
//
// Fix 2026-10-25 (tablero de tareas, fase 2): esta misma clave ahora también
// sirve para dar de alta "Agentes" del tablero de tareas — se le pueden dar
// scopes de tareas (tasks:read/write/comment/import). Sin scopes de tareas
// marcados, la clave sigue comportándose exactamente igual que antes (M2M
// de solo lectura, sin ningún acceso a /api/tasks).
const TASK_SCOPE_OPTIONS = [
  { value: "tasks:read", label: "Leer tareas" },
  { value: "tasks:write", label: "Crear/editar/mover tareas" },
  { value: "tasks:comment", label: "Comentar tareas" },
  { value: "tasks:import", label: "Importar tareas en lote" },
];

function ApiKeysCard() {
  const { token } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [newSecretKeyId, setNewSecretKeyId] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      setKeys(await api.listApiKeys(token));
    } catch {
      toast.error("Error al cargar las claves API");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleScope = (value) => {
    setScopes((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const result = await api.createApiKey(token, name.trim(), scopes);
      setNewSecret(result.api_key);
      setNewSecretKeyId(result.id);
      setName("");
      setScopes([]);
      load();
    } catch {
      toast.error("Error al crear la clave");
    } finally {
      setCreating(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await api.revokeApiKey(token, revokeTarget.id);
      toast.success("Clave revocada");
      load();
    } catch {
      toast.error("Error al revocar la clave");
    } finally {
      setRevokeTarget(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteApiKey(token, deleteTarget.id);
      toast.success("Clave eliminada");
      if (deleteTarget.id === newSecretKeyId) { setNewSecret(null); setNewSecretKeyId(null); }
      load();
    } catch {
      toast.error("Error al eliminar la clave");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Card data-testid="api-keys-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-primary" />
          API y Agentes (Pando / SofIA / tablero de tareas)
        </CardTitle>
        <CardDescription>
          Claves X-API-Key para consumidores de máquina. Sin scopes: solo GET,
          10 rutas permitidas (M2M de solo lectura). Con scopes de tareas: la
          clave actúa como un "Agente" que puede crear, mover y comentar
          tareas en <code className="text-xs">/api/tasks</code> — ver la guía
          en <code className="text-xs">/api/tasks/agent-guide</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="Nombre del consumidor o agente (ej. Alfred, Emergent, Pando SofIA)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="api-key-name-input"
            />
            <Button onClick={handleCreate} disabled={creating || !name.trim()} data-testid="api-key-create-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear clave
            </Button>
          </div>
          <div className="flex flex-wrap gap-3 pl-1">
            {TASK_SCOPE_OPTIONS.map((s) => (
              <label key={s.value} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer" data-testid={`api-key-scope-${s.value}`}>
                <input type="checkbox" checked={scopes.includes(s.value)} onChange={() => toggleScope(s.value)} className="accent-primary" />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        {newSecret && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2" data-testid="api-key-new-secret">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              Copia esta clave ahora — no se podrá volver a ver.
            </p>
            <div className="flex items-center gap-2">
              <code className="text-xs bg-background border rounded px-2 py-1.5 flex-1 break-all" data-testid="api-key-new-secret-value">{newSecret}</code>
              <Button
                size="icon" variant="outline"
                onClick={() => { navigator.clipboard.writeText(newSecret); toast.success("Clave copiada"); }}
                data-testid="api-key-copy-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setNewSecret(null)} data-testid="api-key-hide-secret-btn">Ocultar</Button>
          </div>
        )}

        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No hay claves creadas todavía.</p>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Prefijo</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Último uso</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} data-testid={`api-key-row-${k.id}`}>
                    <TableCell className="font-medium">{k.name}</TableCell>
                    <TableCell><code className="text-xs">{k.prefix}…</code></TableCell>
                    <TableCell>
                      {(k.scopes || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">solo lectura (M2M)</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={k.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                        {k.status === "active" ? "Activa" : "Revocada"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleString("es-ES") : "Nunca"}
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      {k.status === "active" && (
                        <Button size="icon" variant="ghost" title="Revocar" onClick={() => setRevokeTarget(k)} data-testid={`api-key-revoke-${k.id}`}>
                          <Ban className="w-4 h-4 text-amber-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" title="Eliminar" onClick={() => setDeleteTarget(k)} data-testid={`api-key-delete-${k.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(v) => !v && setRevokeTarget(null)}
        title="Revocar clave API"
        description={`"${revokeTarget?.name}" dejará de funcionar de inmediato para cualquier consumidor que la use.`}
        confirmLabel="Revocar"
        onConfirm={confirmRevoke}
        testId="api-key-revoke-confirm-dialog"
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar clave API"
        description={`¿Eliminar "${deleteTarget?.name}" definitivamente? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDelete}
        testId="api-key-delete-confirm-dialog"
      />
    </Card>
  );
}

export default function SettingsPage() {
  const [bases, setBases] = useState({});
  const [selectedBase, setSelectedBase] = useState("");
  const [tables, setTables] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const { token, isAdmin } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [basesData, statusData] = await Promise.all([
          api.getAirtableBases(token).catch(() => ({ bases: {} })),
          api.getIntegrationStatus(token).catch(() => null)
        ]);
        setBases(basesData.bases || {});
        setIntegrationStatus(statusData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (isAdmin) fetchData();
  }, [token, isAdmin]);

  const handleSelectBase = async (baseId) => {
    setSelectedBase(baseId);
    setLoading(true);
    try {
      const data = await api.getAirtableTables(token, baseId);
      setTables(data.tables);
    } catch (error) {
      toast.error("Error al cargar tablas");
    } finally {
      setLoading(false);
    }
  };

  const handleViewRecords = async (tableName) => {
    setLoading(true);
    try {
      const data = await api.getAirtableRecords(token, selectedBase, tableName);
      setRecords(data.records);
      toast.success(`${data.count} registros cargados`);
    } catch (error) {
      toast.error("Error al cargar registros");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToAirtable = async () => {
    setSyncing(true);
    try {
      const result = await api.syncInitiativesToAirtable(token, selectedBase, "Initiatives");
      toast.success(result.message);
    } catch (error) {
      toast.error("Error en sincronización");
    } finally {
      setSyncing(false);
    }
  };

  const handleImportFromAirtable = async () => {
    setSyncing(true);
    try {
      const result = await api.importInitiativesFromAirtable(token, selectedBase, "Initiatives");
      toast.success(result.message);
    } catch (error) {
      toast.error("Error en importación");
    } finally {
      setSyncing(false);
    }
  };

  const handleSendTeamNotification = async () => {
    if (!emailSubject || !emailMessage) {
      toast.error("Completa asunto y mensaje");
      return;
    }
    try {
      await api.notifyTeam(token, emailSubject, emailMessage);
      toast.success("Notificación enviada al equipo");
      setEmailSubject("");
      setEmailMessage("");
    } catch (error) {
      toast.error("Error al enviar notificación");
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6 animate-in">
        <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
        <Card>
          <CardContent className="py-16 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">Acceso Restringido</h3>
            <p className="text-muted-foreground">
              Solo los administradores pueden acceder a la configuración.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configuración</h1>
        <p className="text-muted-foreground mt-1">
          Gestión de integraciones y sincronización
        </p>
      </div>

      {/* Integration Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Estado de Integraciones
          </CardTitle>
          <CardDescription>
            Estado actual de las conexiones externas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <IntegrationCard 
              name="Microsoft Teams" 
              icon={MessageSquare}
              status={integrationStatus?.teams_webhook?.status}
              configured={integrationStatus?.teams_webhook?.configured}
            />
            <IntegrationCard 
              name="Email (Mandrill)" 
              icon={Mail}
              status={integrationStatus?.mandrill_email?.status}
              configured={integrationStatus?.mandrill_email?.configured}
            />
            <IntegrationCard 
              name="Airtable" 
              icon={Database}
              status={integrationStatus?.airtable?.status}
              configured={integrationStatus?.airtable?.configured}
            />
            <IntegrationCard 
              name="Perplexity AI" 
              icon={Sparkles}
              status={integrationStatus?.perplexity_ai?.status}
              configured={integrationStatus?.perplexity_ai?.configured}
            />
            <IntegrationCard 
              name="Scheduler" 
              icon={Clock}
              status={integrationStatus?.scheduler?.status}
              configured={integrationStatus?.scheduler?.configured}
            />
          </div>
        </CardContent>
      </Card>

      {/* API de solo lectura para Pando/SofIA (2026-08-31) */}
      <ApiKeysCard />

      {/* Airtable Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Sincronización Airtable
          </CardTitle>
          <CardDescription>
            Conecta y sincroniza datos con tus bases de Airtable
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Base de Airtable</Label>
              <Select value={selectedBase} onValueChange={handleSelectBase}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una base" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(bases).map(([name, id]) => (
                    <SelectItem key={id} value={id}>
                      {name} ({id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedBase && (
              <div className="space-y-2">
                <Label>Acciones de Sync</Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSyncToAirtable}
                    disabled={syncing}
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Enviar a Airtable
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleImportFromAirtable}
                    disabled={syncing}
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Importar de Airtable
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tables list */}
          {tables.length > 0 && (
            <div className="mt-4">
              <Label className="mb-2 block">Tablas disponibles</Label>
              <div className="flex flex-wrap gap-2">
                {tables.map((table) => (
                  <Button
                    key={table.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewRecords(table.name)}
                    disabled={loading}
                  >
                    {table.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Records preview */}
          {records.length > 0 && (
            <div className="mt-4">
              <Label className="mb-2 block">Preview de registros ({records.length})</Label>
              <div className="max-h-64 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Campos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.slice(0, 10).map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-mono text-xs">
                          {record.id}
                        </TableCell>
                        <TableCell className="text-xs">
                          {Object.entries(record.fields)
                            .slice(0, 3)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ")}
                          ...
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Notificaciones al Equipo
          </CardTitle>
          <CardDescription>
            Envía notificaciones por email a todo el equipo de innovación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-subject">Asunto</Label>
            <Input
              id="email-subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Asunto de la notificación"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-message">Mensaje</Label>
            <textarea
              id="email-message"
              className="w-full min-h-[100px] p-3 border rounded-lg bg-background"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder="Escribe el mensaje para el equipo..."
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>Se enviará a 5 miembros del equipo</span>
            </div>
            <Button onClick={handleSendTeamNotification}>
              <Mail className="w-4 h-4 mr-2" />
              Enviar Notificación
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Connection Status */}
      <Card>
        <CardHeader>
          <CardTitle>Estado de Integraciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                <span>Airtable</span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-lg">🤖</span>
                <span>Perplexity AI</span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span>Mandrill Email</span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                <span>Microsoft Teams</span>
              </div>
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Microsoft Teams Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Microsoft Teams - Alertas Automáticas
          </CardTitle>
          <CardDescription>
            Envía notificaciones y resúmenes al canal de Teams
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button variant="outline" onClick={async () => {
              try {
                const result = await api.testTeamsNotification(token);
                if (result.success) {
                  toast.success("Notificación de prueba enviada a Teams");
                } else {
                  toast.error(result.message || "Error al enviar");
                }
              } catch (e) {
                toast.error("Error al probar Teams");
              }
            }}>
              🧪 Probar conexión Teams
            </Button>
            <Button onClick={async () => {
              try {
                const result = await api.sendTeamsMilestoneUpdate(token);
                if (result.success) {
                  toast.success("Resumen de hitos enviado a Teams");
                } else {
                  toast.error(result.message || "Error al enviar");
                }
              } catch (e) {
                toast.error("Error al enviar a Teams");
              }
            }}>
              📊 Enviar resumen de hitos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Automated Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🤖 Reportes AI Automatizados
          </CardTitle>
          <CardDescription>
            Genera y programa reportes automáticos de innovación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button variant="outline" onClick={async () => {
              try {
                const result = await api.generateAIReport(token, "monthly");
                if (result.success) {
                  toast.success("Informe mensual generado y enviado");
                } else {
                  toast.error(result.message || "Error al generar");
                }
              } catch (e) {
                toast.error("Error al generar informe");
              }
            }}>
              📝 Generar Informe Mensual
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const result = await api.generateAIReport(token, "quarterly");
                if (result.success) {
                  toast.success("Informe trimestral generado y enviado");
                } else {
                  toast.error(result.message || "Error al generar");
                }
              } catch (e) {
                toast.error("Error al generar informe");
              }
            }}>
              📈 Generar Informe Trimestral
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Button onClick={async () => {
              try {
                const result = await api.scheduleAutomatedReports(token, "monthly");
                if (result.success) {
                  toast.success("Reportes mensuales programados");
                }
              } catch (e) {
                toast.error("Error al programar reportes");
              }
            }}>
              🗓️ Programar Reportes Mensuales
            </Button>
            <Button onClick={async () => {
              try {
                const result = await api.scheduleAutomatedReports(token, "quarterly");
                if (result.success) {
                  toast.success("Reportes trimestrales programados");
                }
              } catch (e) {
                toast.error("Error al programar reportes");
              }
            }}>
              📅 Programar Reportes Trimestrales
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
